import { InputRedesigned, Typography } from '@/ui-kit/';
import styles from './name-registartion-form.module.scss';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useMemo, useState } from 'react';
import { type Profile } from '@/types/profile';
import WebApp from '@twa-dev/sdk';
import { eventsApi } from '@/api';
import { callYaMetrika } from '@/utils/call-ya-metrika';
import { registrationStepNameSchema, RegistrationStepNameType } from '@/schemes/registration-step-name-schema';
import { ButtonSubmitStep } from '@/components/form-registration-v3/components/button-submit-step';
import { SuccessNotificationIcon } from '@/assets/icons';
import { useToast } from '@/store/useToast';
import { useTranslation } from 'react-i18next';

interface Props {
    userData?: Profile | null;
    onUpdateUserData: (data: RegistrationStepNameType) => Promise<any>;
    nextStep: () => void;
}

export const NameRegistartionForm: React.FC<Props> = ({ userData, onUpdateUserData, nextStep }) => {
    const showToast = useToast((state) => state.show);
    useEffect(() => {
        callYaMetrika('new_name_screen3');
        eventsApi.updateUserEvent('new_name_screen3');
    }, []);

    const initialValues = useMemo(() => {
        let firstName = '';

        if (WebApp.initDataUnsafe.user) {
            firstName = WebApp.initDataUnsafe.user.first_name;
        }

        if (!userData?.public_name && firstName) {
            callYaMetrika('entered_in_name_field');
        }

        return {
            public_name: userData?.public_name ?? firstName
        };
    }, [userData]);

    const [isLoading, setIsLoading] = useState(false);

    const methods = useForm<RegistrationStepNameType>({
        resolver: zodResolver(registrationStepNameSchema),
        defaultValues: initialValues,
    });
    const {
        register,
        handleSubmit,
        formState: { errors, isDirty, isValid },
        watch,
    } = methods;

    const onSubmit = async (data: RegistrationStepNameType) => {
        try {
            setIsLoading(true);
            /*
             * for update only dirty data use code below
             * if (isDirty) {
             *   await onUpdateUserData(data);
             * }
             */
            await onUpdateUserData(data);

            nextStep();
            eventsApi.updateUserEvent('registration_step_complete_name_screen');
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        eventsApi.updateUserEvent('registration_step_view_name_screen');
    }, [])

    const { t } = useTranslation();

    return (
        <FormProvider {...methods}>
            <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
                <div className={styles.formContainer}>
                    <Typography
                        variant="p"
                        fontWeight="medium"
                        className={styles.titleForm}
                        center>
                        {t('registration.what_is_your_name')}
                    </Typography>
                    <Typography
                        variant="p"
                        fontWeight="medium"
                        className={styles.subtitleForm}
                        center>
                        {t('registration.name_will_be_used_in_questionnaire')}
                    </Typography>
                    <InputRedesigned
                        placeholder={t('your_name')}
                        required
                        {...register('public_name', { required: true })}
                        error={errors?.public_name?.message} />
                </div>
                <ButtonSubmitStep
                    isLoading={isLoading}
                    stepNumber={1}
                    isDisabled={!isValid} />
            </form>
        </FormProvider>
    );
};
